import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../ui/use-toast';
import { climbsService } from '../../services/climbs';
import type { Climb } from '../../types/climb';
import type { User } from '../../types/user';

export type ClimbAdminActionKind = 'confirm' | 'reject' | 'destroy';

interface UseClimbAdminActionsOptions {
  onClimbUpdated?: (updatedClimb: Climb) => void;
  onClimbDeleted?: (climbId: string) => void;
}

/**
 * Whether the reader put this climb up. Its author may edit and delete it as
 * the place's staff can (Api::ClimbsController#allowed_to_edit?), but never
 * confirm or reject it — that is the place vouching for the climb.
 */
export function isClimbAuthor(climb: Climb, user: Pick<User, 'id'> | null | undefined): boolean {
  return !!user && !!climb.created_by && climb.created_by.id === String(user.id);
}

/**
 * What an admin does to a climb — confirm or reject a pending one, edit,
 * delete — with the calls, the toasts and the delete confirm in one place.
 * The buttons that used to own this (ClimbAdminActions) and the swipe strip on
 * a list row both read from here, so they cannot drift apart.
 *
 * `isPending` and `isDiscarded` say which of them apply: confirm and reject to
 * a pending climb, edit and delete to one not yet discarded. A climb that is
 * neither has nothing an admin can do to it from a list — `available` is false.
 */
export function useClimbAdminActions(
  climb: Climb,
  { onClimbUpdated, onClimbDeleted }: UseClimbAdminActionsOptions = {}
) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<ClimbAdminActionKind | null>(null);

  const confirm = async () => {
    try {
      setLoading('confirm');
      const updatedClimb = await climbsService.confirmClimb(climb.id);

      onClimbUpdated?.(updatedClimb);

      toast({
        title: t('climbs.admin.confirmSuccess'),
        description: t('climbs.admin.climbConfirmed', { name: climb.name }),
      });
    } catch (error) {
      console.error('Failed to confirm climb:', error);
      toast({
        title: t('climbs.admin.confirmError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setLoading(null);
    }
  };

  const reject = async () => {
    try {
      setLoading('reject');
      const updatedClimb = await climbsService.rejectClimb(climb.id);

      onClimbUpdated?.(updatedClimb);

      toast({
        title: t('climbs.admin.rejectSuccess'),
        description: t('climbs.admin.climbRejected', { name: climb.name }),
      });
    } catch (error) {
      console.error('Failed to reject climb:', error);
      toast({
        title: t('climbs.admin.rejectError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setLoading(null);
    }
  };

  const destroy = async () => {
    // Confirm before deleting
    if (!window.confirm(t('climbs.admin.destroyConfirm', { name: climb.name }))) {
      return;
    }

    try {
      setLoading('destroy');
      await climbsService.destroyClimb(climb.id);

      onClimbDeleted?.(climb.id);

      toast({
        title: t('climbs.admin.destroySuccess'),
        description: t('climbs.admin.climbDestroyed', { name: climb.name }),
      });
    } catch (error) {
      console.error('Failed to destroy climb:', error);
      toast({
        title: t('climbs.admin.destroyError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setLoading(null);
    }
  };

  const edit = () => {
    const climbTypePath = climb.type === 'Boulder' ? 'boulders' : 'routes';
    navigate(`/gyms/${climb.location.slug}/${climbTypePath}/${climb.slug}/edit`);
  };

  const isPending = climb.official_status === 'pending';
  const isDiscarded = climb.discarded_at !== null;

  return {
    loading,
    confirm,
    reject,
    destroy,
    edit,
    isPending,
    isDiscarded,
    available: isPending || !isDiscarded,
  };
}
